<script lang="ts">
  import { api, type CreditHistoryEntry } from "@/api";
  import { user, token, refreshSessionProfile, logoutAfterPasswordChange } from "@/stores/session";
  import {
    balance,
    serverLimit,
    servers,
    busyAction,
    dailyBonusClaimedToday,
    claimDailyBonus,
    purchaseServerSlot,
  } from "@/stores/dashboard";
  import { confirmModal } from "@/stores/modal";
  import { SERVER_SLOT_COST } from "@/config";
  import { formatNumber } from "@/utils/format";
  import TextInput from "@/components/TextInput.svelte";
  import FormField from "@/components/FormField.svelte";
  import CreditTopUp from "@/components/CreditTopUp.svelte";

  let username = $state("");
  let currentPassword = $state("");
  let newPassword = $state("");
  let confirmPassword = $state("");
  let usernameBusy = $state(false);
  let passwordBusy = $state(false);
  let usernameError = $state("");
  let usernameSuccess = $state("");
  let passwordError = $state("");
  let history = $state<CreditHistoryEntry[]>([]);
  let historyLoading = $state(false);
  let historyError = $state("");
  let historyRefresh = $state(0);
  let initializedUserId = "";

  $effect(() => {
    const currentToken = $token;
    const currentBalance = $balance;
    const refresh = historyRefresh;
    void currentBalance;
    void refresh;
    history = [];
    historyError = "";
    if (!currentToken) {
      return;
    }
    const controller = new AbortController();
    historyLoading = true;
    void api
      .balance(currentToken, controller.signal)
      .then((result) => {
        if (!controller.signal.aborted) {
          history = result.history ?? [];
        }
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) {
          historyError = error instanceof Error ? error.message : "Не удалось загрузить операции";
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          historyLoading = false;
        }
      });
    return () => controller.abort();
  });

  function sourceLabel(source: string): string {
    const labels: Record<string, string> = {
      signup: "Регистрация",
      daily_bonus: "Суточный бонус",
      ad_watch: "Вознаграждение",
      server_slot: "Слот сервера",
      backup_slot: "Слот копии",
      server_charge: "Расход сервера",
      refund: "Возврат",
      admin_adjust: "Корректировка баланса",
      top_up: "Пополнение баланса",
    };
    return labels[source] ?? "Операция с балансом";
  }

  $effect(() => {
    if ($user && $user.id !== initializedUserId) {
      initializedUserId = $user.id;
      username = $user.username;
    }
  });

  const role = $derived(
    $user?.role === "admin"
      ? "Администратор"
      : $user?.role === "operator"
        ? "Оператор"
        : "Пользователь",
  );

  function date(value?: string | null): string {
    if (!value) {
      return "Нет данных";
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime())
      ? "Нет данных"
      : parsed.toLocaleString("ru-RU", {
          day: "numeric",
          month: "long",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
  }

  async function saveUsername(event: SubmitEvent) {
    event.preventDefault();
    if (!$token || usernameBusy) {
      return;
    }
    usernameError = "";
    usernameSuccess = "";
    usernameBusy = true;
    try {
      await api.updateProfile($token, username.trim());
      await refreshSessionProfile();
      usernameSuccess = "Имя сохранено";
    } catch (error) {
      usernameError = error instanceof Error ? error.message : "Не удалось сохранить имя";
    } finally {
      usernameBusy = false;
    }
  }

  async function savePassword(event: SubmitEvent) {
    event.preventDefault();
    if (!$token || passwordBusy) {
      return;
    }
    passwordError = "";
    if (newPassword !== confirmPassword) {
      passwordError = "Новые пароли не совпадают";
      return;
    }
    passwordBusy = true;
    try {
      await api.changePassword($token, currentPassword, newPassword);
      currentPassword = "";
      newPassword = "";
      confirmPassword = "";
      logoutAfterPasswordChange();
    } catch (error) {
      passwordError = error instanceof Error ? error.message : "Не удалось изменить пароль";
    } finally {
      passwordBusy = false;
    }
  }

  async function buySlot() {
    if (
      await confirmModal(
        "Купить дополнительный слот?",
        `Стоимость: ${SERVER_SLOT_COST} кредитов.`,
        "Купить слот",
      )
    ) {
      await purchaseServerSlot();
    }
  }
</script>

{#if $user}
  <div class="account-page">
    <header class="account-heading">
      <p class="eyebrow">Личный кабинет</p>
      <h1>Аккаунт</h1>
      <p>Данные профиля, баланс и настройки входа.</p>
    </header>

    <div class="account-grid">
      <section class="panel-card account-card" aria-labelledby="account-details-title">
        <h2 id="account-details-title">Данные аккаунта</h2>
        <dl class="details">
          <div>
            <dt>Номер аккаунта</dt>
            <dd class="account-id">{$user.id}</dd>
          </div>
          <div>
            <dt>Имя</dt>
            <dd>{$user.username}</dd>
          </div>
          <div>
            <dt>Электронная почта</dt>
            <dd>{$user.email || "Нет данных"}</dd>
          </div>
          <div>
            <dt>Роль</dt>
            <dd>{role}</dd>
          </div>
          <div>
            <dt>Дата регистрации</dt>
            <dd>{date($user.createdAt)}</dd>
          </div>
          <div>
            <dt>Последний вход</dt>
            <dd>{date($user.lastLoginAt)}</dd>
          </div>
        </dl>
      </section>

      <section class="panel-card account-card" aria-labelledby="account-balance-title">
        <h2 id="account-balance-title">Баланс и серверы</h2>
        <p class="balance">{formatNumber($balance)} <span>кр.</span></p>
        <p class="muted">Серверы: {$servers.length} из {$serverLimit}</p>
        <div class="actions">
          {#if $dailyBonusClaimedToday}
            <span class="muted">Суточный бонус получен</span>
          {:else}
            <button
              class="button"
              type="button"
              disabled={!!$busyAction}
              onclick={() => void claimDailyBonus()}
            >
              {$busyAction === "daily-bonus" ? "Получаем…" : "Получить суточный бонус"}
            </button>
          {/if}
          <button
            class="ghost-button slot-button"
            type="button"
            disabled={!!$busyAction || $balance < SERVER_SLOT_COST}
            onclick={buySlot}
          >
            {$busyAction === "server-slot" ? "Покупаем…" : `Купить слот за ${SERVER_SLOT_COST} кр.`}
          </button>
        </div>
      </section>

      <div class="history-card">
        <CreditTopUp oncredited={() => historyRefresh++} />
      </div>

      <section class="panel-card account-card history-card" aria-labelledby="account-history-title">
        <h2 id="account-history-title">Последние операции</h2>
        {#if historyLoading}
          <p class="muted" role="status">Загружаем операции…</p>
        {:else if historyError}
          <p class="form-error" role="alert">{historyError}</p>
          <button class="ghost-button slot-button" type="button" onclick={() => historyRefresh++}
            >Повторить</button
          >
        {:else if history.length === 0}
          <p class="muted">Операций пока нет</p>
        {:else}
          <ul class="history">
            {#each history as entry (entry.id)}
              <li>
                <span
                  ><strong>{sourceLabel(entry.source)}</strong><small>{date(entry.createdAt)}</small
                  ></span
                >
                <strong class:positive={entry.amount > 0}
                  >{entry.amount > 0 ? "+" : ""}{formatNumber(entry.amount)} кр.</strong
                >
              </li>
            {/each}
          </ul>
        {/if}
      </section>

      <section class="panel-card account-card" aria-labelledby="account-name-title">
        <h2 id="account-name-title">Имя пользователя</h2>
        <form onsubmit={saveUsername}>
          <FormField
            label="Имя пользователя"
            hint="От 3 до 32 символов: латинские буквы, цифры, точка, дефис и подчёркивание."
          >
            <TextInput
              bind:value={username}
              required
              minlength={3}
              maxlength={32}
              pattern="[a-zA-Z0-9_.-]+"
              autocomplete="username"
              disabled={usernameBusy}
            />
          </FormField>
          {#if usernameError}<p class="form-error" role="alert">{usernameError}</p>{/if}
          {#if usernameSuccess}<p class="form-success" role="status">{usernameSuccess}</p>{/if}
          <button
            class="button"
            type="submit"
            disabled={usernameBusy || !username.trim() || username.trim() === $user.username}
          >
            {usernameBusy ? "Сохраняем…" : "Сохранить имя"}
          </button>
        </form>
      </section>

      <section class="panel-card account-card" aria-labelledby="account-password-title">
        <h2 id="account-password-title">Пароль</h2>
        <form onsubmit={savePassword}>
          <FormField label="Текущий пароль">
            <TextInput
              type="password"
              bind:value={currentPassword}
              required
              autocomplete="current-password"
              disabled={passwordBusy}
            />
          </FormField>
          <FormField label="Новый пароль" hint="От 8 до 128 символов.">
            <TextInput
              type="password"
              bind:value={newPassword}
              required
              minlength={8}
              maxlength={128}
              autocomplete="new-password"
              disabled={passwordBusy}
            />
          </FormField>
          <FormField label="Повторите новый пароль">
            <TextInput
              type="password"
              bind:value={confirmPassword}
              required
              minlength={8}
              maxlength={128}
              autocomplete="new-password"
              disabled={passwordBusy}
            />
          </FormField>
          {#if passwordError}<p class="form-error" role="alert">{passwordError}</p>{/if}
          <button class="button" type="submit" disabled={passwordBusy}>
            {passwordBusy ? "Сохраняем…" : "Изменить пароль"}
          </button>
        </form>
      </section>
    </div>
  </div>
{/if}

<style>
  .account-heading {
    margin-bottom: 2rem;
  }
  .account-heading h1 {
    margin: 0.6rem 0;
    font: 500 clamp(1.8rem, 4vw, 3rem)/1.1 var(--font-display);
    text-transform: uppercase;
  }
  .account-heading > p:last-child,
  .muted {
    color: var(--muted);
  }
  .account-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;
  }
  .account-card {
    min-width: 0;
    padding: 1.5rem;
  }
  .account-card h2 {
    margin: 0 0 1.4rem;
    font: 500 1.2rem/1.2 var(--font-display);
    text-transform: uppercase;
  }
  .details {
    display: grid;
    gap: 0.9rem;
    margin: 0;
  }
  .details div {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1.4fr);
    gap: 0.75rem;
  }
  dt {
    color: var(--muted);
  }
  dd {
    margin: 0;
    overflow-wrap: anywhere;
  }
  .history-card {
    grid-column: 1 / -1;
  }
  .history {
    display: grid;
    gap: 0;
    margin: 0;
    padding: 0;
    list-style: none;
  }
  .history li {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.8rem 0;
    border-top: 1px solid var(--line);
  }
  .history li > span {
    display: grid;
    gap: 0.3rem;
  }
  .history small {
    color: var(--muted);
  }
  .history .positive {
    color: var(--green);
  }
  .account-id {
    font-family: monospace;
  }
  .balance {
    margin: 0 0 0.5rem;
    color: var(--cream);
    font: 500 2.2rem/1 var(--font-display);
  }
  .balance span {
    color: var(--muted);
    font-size: 1rem;
  }
  .actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.75rem;
    margin-top: 1.4rem;
  }
  .slot-button {
    border: 1px solid var(--line-strong);
    padding: 0.65rem 0.85rem;
  }
  form {
    display: grid;
    gap: 1rem;
  }
  form .button {
    justify-self: start;
  }
  .form-error,
  .form-success {
    margin: 0;
    overflow-wrap: anywhere;
  }
  .form-error {
    color: var(--mars-light);
  }
  .form-success {
    color: var(--green);
  }
  @media (max-width: 700px) {
    .account-grid {
      grid-template-columns: 1fr;
    }
  }
  @media (max-width: 420px) {
    .account-card {
      padding: 1rem;
    }
    .details div {
      grid-template-columns: 1fr;
      gap: 0.25rem;
    }
  }
</style>
